/**
 * DebouncedColorInput Component
 *
 * A color picker with optional opacity slider that debounces updates
 * to prevent UI lag during rapid color selection.
 *
 * The component maintains local state for immediate visual feedback,
 * then propagates changes to the parent after a 150ms debounce delay.
 *
 * @param {Object} props
 * @param {string} props.value - Current color value (hex format)
 * @param {number} props.opacity - Current opacity (0.1 to 1.0)
 * @param {Function} props.onChange - Callback when color changes
 * @param {Function} props.onOpacityChange - Callback when opacity changes
 */

import { useEffect, useRef, useState } from 'react';
import { ColorInput } from './StyledMatrixFilterComponents';

/** Debounce delay in milliseconds for color/opacity updates */
const DEBOUNCE_MS = 150;

const DebouncedColorInput = ({ value, opacity, onChange, onOpacityChange, ...props }) => {
	const [localValue, setLocalValue] = useState(value);
	const [localOpacity, setLocalOpacity] = useState(opacity);
	const colorTimeoutRef = useRef(null);
	const opacityTimeoutRef = useRef(null);

	// Sync with external value changes
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	useEffect(() => {
		setLocalOpacity(opacity);
	}, [opacity]);

	const handleColorChange = e => {
		const newValue = e.target.value;
		setLocalValue(newValue);

		// Debounce the actual state update
		if (colorTimeoutRef.current) {
			clearTimeout(colorTimeoutRef.current);
		}
		colorTimeoutRef.current = setTimeout(() => {
			onChange?.(newValue);
		}, DEBOUNCE_MS);
	};

	const handleOpacityChange = e => {
		const newOpacity = parseFloat(e.target.value);
		setLocalOpacity(newOpacity);

		// Debounce the actual state update
		if (opacityTimeoutRef.current) {
			clearTimeout(opacityTimeoutRef.current);
		}
		opacityTimeoutRef.current = setTimeout(() => {
			onOpacityChange?.(newOpacity);
		}, DEBOUNCE_MS);
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (colorTimeoutRef.current) clearTimeout(colorTimeoutRef.current);
			if (opacityTimeoutRef.current) clearTimeout(opacityTimeoutRef.current);
		};
	}, []);

	return (
		<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
			<ColorInput type="color" value={localValue} onChange={handleColorChange} {...props} />
			{onOpacityChange && (
				<input
					type="range"
					min="0.1"
					max="1"
					step="0.1"
					value={localOpacity}
					onChange={handleOpacityChange}
					title={`Opacity: ${Math.round(localOpacity * 100)}%`}
					style={{
						width: '40px',
						height: '4px',
						cursor: 'pointer',
						accentColor: localValue,
					}}
				/>
			)}
		</div>
	);
};

export default DebouncedColorInput;
